import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Users, Settings, UserPlus, Trash2, Shield, Crown, Mail, Clock, Tag, Plus } from 'lucide-react';
import clsx from 'clsx';
import { useOrganization } from '@/context/OrganizationContext';
import { useAuth } from '@/context/AuthContext';
import { getOrgMembers, getOrgPendingInvites, updateOrganization, removeOrgMember, updateOrgMemberRole, inviteOrgMemberByEmail, cancelOrgInvite, type OrgMember, type OrgInvite } from '@/db/organizations';
import { supabase } from '@/lib/supabaseClient';
import { generateA11yId } from '@/utils/accessibility';
import { handleError } from '@/utils/errorHandler';
import { TagsManager } from '@/components/fields/TagsManager';
import { fetchAllTaskTagsForOrg, fetchTags, createTag, type TagDefinition } from '@/db/tags';

interface OrgSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OrgSettingsModal({ isOpen, onClose }: OrgSettingsModalProps) {
  const { currentOrg, refreshOrgs } = useOrganization();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'general' | 'members' | 'tags'>('general');
  const [name, setName] = useState('');
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<OrgInvite[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orphanTags, setOrphanTags] = useState<string[]>([]);
  const [orgTags, setOrgTags] = useState<TagDefinition[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagManagerKey, setTagManagerKey] = useState(0);

  const modalLabelId = useMemo(() => generateA11yId('org-settings-label'), []);

  const isOwner = useMemo(() => {
    if (!currentOrg || !user) return false;
    return members.some(m => m.user_id === user.id && m.role === 'owner');
  }, [members, currentOrg, user]);

  const isAdmin = useMemo(() => {
    if (!currentOrg || !user) return false;
    return members.some(m => m.user_id === user.id && (m.role === 'owner' || m.role === 'admin'));
  }, [members, currentOrg, user]);

  const loadOrphanTags = useCallback(async () => {
    if (!currentOrg) return;
    setTagsLoading(true);
    try {
      const [allTaskTags, definedTags] = await Promise.all([
        fetchAllTaskTagsForOrg(currentOrg.id),
        fetchTags('organization', currentOrg.id),
      ]);
      setOrgTags(definedTags);
      const definedNames = new Set(definedTags.map(t => t.name.toLowerCase()));
      setOrphanTags(allTaskTags.filter(t => !definedNames.has(t.toLowerCase())));
    } catch (err) {
      handleError(err, { level: 'warning', message: 'Errore caricamento tag' });
    } finally {
      setTagsLoading(false);
    }
  }, [currentOrg]);

  const loadMembers = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const [m, invites] = await Promise.all([
        getOrgMembers(currentOrg.id),
        getOrgPendingInvites(currentOrg.id),
      ]);
      setMembers(m);
      setPendingInvites(invites);
    } catch (err) {
      handleError(err, { level: 'warning', message: 'Error loading team' });
    }
  }, [currentOrg]);

  useEffect(() => {
    if (isOpen && currentOrg) {
      setName(currentOrg.name);
      setActiveTab('general');
      setError(null);
      setInviteEmail('');
      loadMembers();
    }
  }, [isOpen, currentOrg, loadMembers]);

  useEffect(() => {
    if (isOpen && activeTab === 'tags') {
      loadOrphanTags();
    }
  }, [isOpen, activeTab, loadOrphanTags]);

  const handlePromoteTag = async (tagName: string) => {
    if (!currentOrg) return;
    try {
      await createTag({
        name: tagName,
        color: null,
        scope: 'organization',
        scope_id: currentOrg.id,
        order_index: orgTags.length,
        created_at: Date.now(),
      });
      setOrphanTags(prev => prev.filter(t => t !== tagName));
      // Refresh the TagsManager by bumping its key
      setTagManagerKey(k => k + 1);
      await loadOrphanTags();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore promozione tag' });
    }
  };

  const handlePromoteAll = async () => {
    if (!currentOrg || orphanTags.length === 0) return;
    try {
      await Promise.all(orphanTags.map((tagName, i) =>
        createTag({
          name: tagName,
          color: null,
          scope: 'organization',
          scope_id: currentOrg.id,
          order_index: orgTags.length + i,
          created_at: Date.now(),
        })
      ));
      setOrphanTags([]);
      setTagManagerKey(k => k + 1);
      await loadOrphanTags();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore promozione tag' });
    }
  };

  const handleSaveName = async () => {
    if (!currentOrg || !name.trim() || saving) return;
    setSaving(true);
    try {
      await updateOrganization(currentOrg.id, { name: name.trim() });
      await refreshOrgs();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nel salvataggio' });
      setError(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!currentOrg || !inviteEmail.trim() || inviting) return;
    setInviting(true);
    setError(null);
    try {
      await inviteOrgMemberByEmail(currentOrg.id, inviteEmail.trim());
      supabase.functions.invoke('send-invite-email', {
        body: { email: inviteEmail.trim(), orgName: currentOrg.name, invitedByName: user?.user_metadata?.full_name || 'Un membro', role: 'member' }
      }).catch(console.error);
      setInviteEmail('');
      await loadMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nell\'invito');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!currentOrg) return;
    try {
      await removeOrgMember(currentOrg.id, userId);
      await loadMembers();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nella rimozione' });
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!currentOrg) return;
    try {
      await updateOrgMemberRole(currentOrg.id, userId, newRole);
      await loadMembers();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nel cambio ruolo' });
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await cancelOrgInvite(inviteId);
      await loadMembers();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nella cancellazione' });
    }
  };

  const roleIcon = (role: string) => {
    if (role === 'owner') return <Crown className="w-3.5 h-3.5 text-amber-500" />;
    if (role === 'admin') return <Shield className="w-3.5 h-3.5 text-accent-violet" />;
    return null;
  };

  const getInitials = (member: OrgMember) => {
    if (member.display_name) {
      return member.display_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    }
    return member.user_id.substring(0, 2).toUpperCase();
  };

  if (!isOpen || !currentOrg) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalLabelId}
        className="relative w-full max-w-3xl max-h-[85vh] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in flex overflow-hidden"
      >
        {/* Sidebar */}
        <div className="w-52 bg-surface-tertiary/50 border-r border-border-primary p-3 flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-sm overflow-hidden flex-shrink-0">
              {currentOrg.avatar_url ? (
                <img src={currentOrg.avatar_url} alt="" className="w-7 h-7 rounded-lg object-cover" />
              ) : (
                <span className="text-[10px] font-bold text-white">{currentOrg.name?.[0]?.toUpperCase() || 'Z'}</span>
              )}
            </div>
            <span className="text-[14px] font-semibold text-text-primary truncate">{currentOrg.name}</span>
          </div>

          <nav className="space-y-1">
            <button
              onClick={() => setActiveTab('general')}
              className={clsx(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all',
                activeTab === 'general' ? 'bg-accent-violet/10 text-accent-violet' : 'text-text-tertiary hover:bg-surface-hover hover:text-text-primary'
              )}
            >
              <Settings className="w-4 h-4" />
              Generale
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={clsx(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all',
                activeTab === 'members' ? 'bg-accent-violet/10 text-accent-violet' : 'text-text-tertiary hover:bg-surface-hover hover:text-text-primary'
              )}
            >
              <Users className="w-4 h-4" />
              Membri ({members.length})
            </button>
            <button
              onClick={() => setActiveTab('tags')}
              className={clsx(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-all',
                activeTab === 'tags' ? 'bg-accent-violet/10 text-accent-violet' : 'text-text-tertiary hover:bg-surface-hover hover:text-text-primary'
              )}
            >
              <Tag className="w-4 h-4" />
              Tag globali
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
            <h2 id={modalLabelId} className="text-lg font-semibold text-text-primary">
              {activeTab === 'general' ? 'Impostazioni organizzazione' : activeTab === 'members' ? 'Membri' : 'Tag globali'}
            </h2>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-primary">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Nome organizzazione</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="flex-1 px-4 py-2.5 rounded-lg border border-border-primary bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent"
                      disabled={!isAdmin}
                    />
                    {isAdmin && (
                      <button
                        onClick={handleSaveName}
                        disabled={saving || name === currentOrg.name}
                        className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-accent-violet hover:bg-accent-violet/90 disabled:opacity-50 transition-colors"
                      >
                        {saving ? 'Salvataggio...' : 'Salva'}
                      </button>
                    )}
                  </div>
                  {error && activeTab === 'general' && <p className="mt-2 text-sm text-accent-rose">{error}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Slug</label>
                  <p className="text-sm text-text-quaternary font-mono bg-surface-primary px-4 py-2.5 rounded-lg border border-border-primary">{currentOrg.slug}</p>
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div className="space-y-6">
                {/* Invite by email */}
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Invita per email</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => { setInviteEmail(e.target.value); setError(null); }}
                        placeholder="email@esempio.com"
                        className="flex-1 px-4 py-2.5 rounded-lg border border-border-primary bg-surface-primary text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
                      />
                      <button
                        onClick={handleInvite}
                        disabled={!inviteEmail.trim() || inviting}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-accent-violet hover:bg-accent-violet/90 disabled:opacity-50 transition-colors"
                      >
                        <UserPlus className="w-4 h-4" />
                        {inviting ? 'Invito...' : 'Invita'}
                      </button>
                    </div>
                    {error && activeTab === 'members' && <p className="mt-2 text-sm text-accent-rose">{error}</p>}
                  </div>
                )}

                {/* Members list */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-text-secondary">Membri ({members.length})</h3>
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-surface-primary border border-border-primary">
                      <div className="flex items-center gap-3">
                        {member.avatar_url ? (
                          <img src={member.avatar_url} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-accent-violet/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-medium text-accent-violet">{getInitials(member)}</span>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-text-primary">
                            {member.display_name || member.email || member.user_id.substring(0, 8)}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {roleIcon(member.role)}
                            <span className="text-xs text-text-quaternary capitalize">{member.role}</span>
                            {member.email && member.display_name && (
                              <span className="text-xs text-text-quaternary ml-1">{member.email}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && member.user_id !== user?.id && member.role !== 'owner' && (
                          <>
                            <select
                              value={member.role}
                              onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                              className="text-xs bg-surface-secondary border border-border-primary rounded px-2 py-1 text-text-primary"
                            >
                              <option value="member">Membro</option>
                              <option value="admin">Admin</option>
                              {isOwner && <option value="owner">Owner</option>}
                            </select>
                            <button
                              onClick={() => handleRemoveMember(member.user_id)}
                              className="p-1.5 rounded-md hover:bg-accent-rose/10 text-text-quaternary hover:text-accent-rose transition-colors"
                              title="Rimuovi membro"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        {member.user_id === user?.id && (
                          <span className="text-xs text-text-quaternary italic">Tu</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pending invites */}
                {pendingInvites.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-text-secondary">Inviti pendenti</h3>
                    {pendingInvites.map((invite) => (
                      <div key={invite.id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-surface-primary border border-border-primary">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0">
                            <Mail className="w-4 h-4 text-text-quaternary" />
                          </div>
                          <div>
                            <p className="text-sm text-text-tertiary">{invite.email}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3 text-text-quaternary" />
                              <span className="text-xs text-text-quaternary">In attesa</span>
                            </div>
                          </div>
                        </div>
                        {isAdmin && (
                          <button
                            onClick={() => handleCancelInvite(invite.id)}
                            className="p-1.5 rounded-md hover:bg-accent-rose/10 text-text-quaternary hover:text-accent-rose transition-colors"
                            title="Annulla invito"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tags' && (
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-text-tertiary mb-4">
                    I tag globali sono disponibili in tutti gli spazi e liste dell'organizzazione.
                  </p>
                  <TagsManager
                    key={tagManagerKey}
                    scope="organization"
                    scopeId={currentOrg.id}
                  />
                </div>

                {/* Orphan tags — used on tasks but not defined */}
                {!tagsLoading && orphanTags.length > 0 && (
                  <div className="pt-4 border-t border-border-primary/50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-medium text-text-secondary">Tag in uso non definiti</h3>
                        <p className="text-xs text-text-quaternary mt-0.5">
                          Questi tag sono usati sui task ma non sono definiti come tag globali.
                        </p>
                      </div>
                      <button
                        onClick={handlePromoteAll}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-accent-violet border border-accent-violet/30 hover:bg-accent-violet/10 transition-colors"
                      >
                        Aggiungi tutti
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {orphanTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => handlePromoteTag(tag)}
                          className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-border-primary/50 bg-surface-primary hover:border-accent-violet/50 hover:bg-accent-violet/5 transition-colors"
                          title={`Aggiungi "${tag}" come tag globale`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-text-quaternary shrink-0" />
                          {tag}
                          <Plus className="w-3 h-3 text-text-quaternary group-hover:text-accent-violet transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
